import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { useTranslation } from "react-i18next";

export default function ActivitySheet({ boardId, open, onClose }) {
  const { t, i18n } = useTranslation();
  const dfLocale = i18n.language?.startsWith("en") ? undefined : es;
  const ACTION_TEXT = {
    created: t("activity.actions.created"),
    updated: t("activity.actions.updated"),
    deleted: t("activity.actions.deleted"),
    moved: t("activity.actions.moved"),
  };

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["activity", boardId],
    queryFn: async () => (await api.get(`/boards/${boardId}/activity`, { params: { limit: 100 } })).data,
    enabled: open && !!boardId,
  });

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent className="glass-panel border-white/40 dark:border-white/10 w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-display">{t("activity.title")}</SheetTitle>
          <SheetDescription className="sr-only">{t("activity.desc")}</SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-3">
          {isLoading && <p className="text-sm text-muted-foreground">{t("activity.loading")}</p>}
          {!isLoading && items.length === 0 && <p className="text-sm text-muted-foreground">{t("activity.empty")}</p>}
          {items.map((a) => (
            <div key={a.activity_id} className="flex items-start gap-3 glass-card rounded-xl p-3" data-testid={`activity-${a.activity_id}`}>
              <div className="h-8 w-8 rounded-full bg-blue-500/20 grid place-items-center text-xs font-semibold">{a.actor?.name?.[0]?.toUpperCase() || "U"}</div>
              <div className="flex-1 text-sm">
                <p>
                  <span className="font-medium">{a.actor?.name || t("activity.someone")}</span>{" "}
                  <span className="text-muted-foreground">{ACTION_TEXT[a.action] || a.action}</span>{" "}
                  <span>{a.entity_type}</span>
                  {a.metadata?.title && <span className="ml-1">«{a.metadata.title}»</span>}
                  {a.metadata?.name && <span className="ml-1">«{a.metadata.name}»</span>}
                </p>
                <p className="text-xs text-muted-foreground">{formatDistanceToNow(new Date(a.created_at), { addSuffix: true, locale: dfLocale })}</p>
              </div>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
